import _ from "lodash";
import { scaleLinear } from "d3";
import constants from '../utils/constants';

export const getAgeKey = (d) => {
    if (d.Section === 'Age') return `${d.Group}`;
    else return `${d.Section}`;
};

export const getAgeDropdownKey = (d) => {
    if (d.Section === 'Age') return `Age ${d.Group}`;
    else return `${d.Section} (Age ${d.Group})`;
};

export const getDataPointKey = (d) => {
    return `${d.ChemicalSubstances}${d.Matrix}${d.Units}`;
};

export const mapPointsToErrorStrings = (data_points, selected_chemical_substance, selected_survey_cycle, 
        selected_matrix, selected_age, midpoint) => {

    return _.reduce(data_points, (result, data_point) => {
        const data_point_errors = getErrorStrings(
            data_point, selected_chemical_substance, selected_survey_cycle, 
            selected_matrix, selected_age, midpoint);

        return {
            missing_data_strings: _.concat(result.missing_data_strings, data_point_errors.missing_data_strings),
            unreliable_data_strings: _.concat(result.unreliable_data_strings, data_point_errors.unreliable_data_strings)
        };
    }, { missing_data_strings: [], unreliable_data_strings: []});

};
export const getErrorStrings = (d, selected_chemical_substance, selected_survey_cycle, selected_matrix, selected_age, midpoint) => {
    const missing_data_strings = [];
    const unreliable_data_strings = [];

    // missing data_string center
    if (d.center_flag === 'F')
        missing_data_strings.push(
            `${d.region} Data is missing for ${selected_chemical_substance} - Cycle ${selected_survey_cycle}
            - ${selected_matrix.title} Concentrations - ${_.get(selected_age, 'dropdown', '')} - ${midpoint} and 
            therefore the data is not shown`
        );
    // unreliable data_string center
    if (d.center_flag === 'E')
        unreliable_data_strings.push(
            `Although charted, the ${midpoint.toLowerCase()} ${d.region} data 
            is statistically unreliable`
        );
    
    // missing data_strings lower+upper
    let percentile_error_string = null;
    if (d.lower_flag === 'F' && d.upper_flag === 'F') percentile_error_string = `${d.lower_type}th & ${d.upper_type}th`;
    else if (d.lower_flag === 'F') percentile_error_string = `${d.lower_type}th`;
    else if (d.upper_flag === 'F') percentile_error_string = `${d.upper_type}th`;
    if (percentile_error_string) 
        missing_data_strings.push(
            `${d.region} Data is missing for ${selected_chemical_substance} - Cycle ${selected_survey_cycle}
            - ${selected_matrix.title} Concentrations - ${_.get(selected_age, 'dropdown', '')} - ${percentile_error_string} percentile 
            and therefore the data is not shown`
        );

    // unreliable data_string lower+upper
    let percentile_unreliable_string = null;
    if (d.lower_flag === 'E' && d.upper_flag === 'E') percentile_unreliable_string = `${d.lower_type}th & ${d.upper_type}th`;
    else if (d.lower_flag === 'E') percentile_unreliable_string = `${d.lower_type}th`;
    else if (d.upper_flag === 'E') percentile_unreliable_string = `${d.upper_type}th`;
    if (percentile_unreliable_string) 
        unreliable_data_strings.push(
            `Although charted, the ${percentile_unreliable_string} percentile ${d.region} data is statistically unreliable`
        );

    return { missing_data_strings, unreliable_data_strings };
};

export const getMaxFromDataPoints = (data, midpoint) => {
    const max_data_vals = _.map(data, (data_point) => {
        return _.isNull(data_point.UpperPercentile) 
            ? (_.isUndefined(data_point[midpoint]) ? -Infinity : data_point[midpoint])
            : data_point.UpperPercentile;
    });

    return _.max(max_data_vals);
};

export const getMinFromDataPoints = (data, midpoint) => {
    const min_data_vals = _.map(data, (data_point) => {
        return _.isNull(data_point.LowerPercentile) 
            ? (_.isNaN(data_point[midpoint]) ? Infinity : data_point[midpoint])
            : data_point.LowerPercentile;
    });

    return _.min(min_data_vals);
};

export const getNiceDomain = (min, max) => {
    if (min === max) {
        min -= min / 10;
        max += max / 10;
    }
    const ticks = scaleLinear().domain([min, max]).ticks(constants.num_axis_ticks) 

    const min_tick = ticks[0];
    const max_tick = ticks[ticks.length - 1];

    // round to second decimal point to calculate tick_diff without floating point
    // errors (e.g. 0.6 - 0.4 !== 0.2 unforunately)
    const tick_diff = (parseInt(ticks[1] * 100) - parseInt(ticks[0] * 100)) / 100;

    if (min < min_tick) min = min_tick - tick_diff;
    if (max > max_tick) max = max_tick + tick_diff;
    
    return [min, max];
};

export const parseRegionDataHorizontal = (data, midpoint, selected_cycle) => {
    // parses region data into required fields
    return _(data)
        .values()
        .map((data_point) => {
            return {
                lower: data_point.LowerPercentile,
                lower_flag: data_point.LowerPercentileFlag,
                lower_type: data_point.LowerPercentileType,
                upper: data_point.UpperPercentile,
                upper_flag: data_point.UpperPercentileFlag,
                upper_type: data_point.UpperPercentileType,
                center: data_point[midpoint],
                center_flag: data_point[`${midpoint}Flag`],
                static: getAgeKey(data_point),
                section: data_point.Section,
                striped: data_point.LowerPercentileType === 25 || data_point.UpperPercentileType === 75,
                lod: data_point.LOD,
                cycle: data_point.Cycle === 'active' ? selected_cycle : data_point.Cycle,
                region: data_point.Region
            }
        })
        .value();
}

export const parseRegionDataVertical = (data, midpoint, selected_cycle) => {
    // parses region data into required fields
    return _(data)
        .values()
        .map((data_point) => {
            return {
                lower: data_point.LowerPercentile,
                lower_flag: data_point.LowerPercentileFlag,
                lower_type: data_point.LowerPercentileType,
                upper: data_point.UpperPercentile,
                upper_flag: data_point.UpperPercentileFlag,
                upper_type: data_point.UpperPercentileType,
                center: data_point[midpoint],
                center_flag: data_point[`${midpoint}Flag`],
                static: data_point.Cycle === 'active' ? selected_cycle : data_point.Cycle,
                striped: data_point.LowerPercentileType === 25 || data_point.UpperPercentileType === 75,
                lod: data_point.LOD,
                age_group: getAgeKey(data_point),
                region: data_point.Region
            }
        })
        .value();
}

const data_order = {
    'All': 1,
    'Males': 2,
    'Females': 3,
    'Age': 4
};
export const sortAgesBySection = (data, age_property) => {
    return _.sortBy(data, [
        // first sort
        (data_point) => {
            return data_order[data_point.section];
        }, 
        // second sort
        (data_point) => {
            if (!_.get(data_point, age_property, null)) return 0;
            const num_regex = /\d*/;
            // return first ocurring number
            return parseInt(_.get(data_point[age_property].match(num_regex), '0', 0));
        }
    ]);
};